import type { MouseEvent } from "react";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Flex } from "orcs-design-system";

import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import { isPlainLeftClick, TreeRowLinkLabel } from "src/shared/TreeViewNext";

import type { TeamTreeLinkNode } from "./getTeamTreeLinkTo";

type GroupNode = TeamTreeLinkNode & {
  name?: string | undefined;
  isPlaceholder?: boolean;
  group?: {
    id?: string;
    type?: string;
    isHidden?: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const GroupsTreeHeaderLinkBase = styled(Flex).attrs({ as: "a" })`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: none;
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  overflow: hidden;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

const GroupsTreeHeaderTitleLink = styled(GroupsTreeHeaderLinkBase)<{
  $isHidden?: boolean | undefined;
}>`
  text-decoration: ${({ $isHidden }) => ($isHidden ? "line-through" : "none")};
  min-width: 0;
  flex: 1;
`;

const GroupsTreeHeaderBadgeLink = styled(GroupsTreeHeaderLinkBase)`
  flex: 0 0 auto;
  min-width: auto;
`;

type GroupsTreeHeaderProps = {
  node: GroupNode;
  groupTypes?: Record<string, unknown>;
  onActivate?: () => void;
  getTeamLinkTo?: (node: TeamTreeLinkNode) => string | null;
};

const GroupsTreeHeader = ({
  node,
  groupTypes,
  onActivate,
  getTeamLinkTo,
}: GroupsTreeHeaderProps) => {
  const { group } = node;
  const teamHref = getTeamLinkTo?.(node) ?? null;
  const isHidden = Boolean(group?.isHidden);

  const handleLinkClick = (e: MouseEvent) => {
    if (!isPlainLeftClick(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    onActivate?.();
  };

  const label = (
    <TreeRowLinkLabel
      data-testid="treeheader-node-name"
      data-tree-row-content
      $useSubTextColor={Boolean(node.isPlaceholder || isHidden)}
      $fillRow
      title={typeof node.name === "string" ? node.name : undefined}
    >
      {node.name}
    </TreeRowLinkLabel>
  );

  return (
    <Flex alignItems="center" width="100%" gap="xs" minWidth={0}>
      {teamHref ? (
        <GroupsTreeHeaderTitleLink
          className="treeHeaderTitle"
          data-tree-row-action
          href={teamHref}
          $isHidden={isHidden}
          onClick={(e) => handleLinkClick(e)}
        >
          {label}
        </GroupsTreeHeaderTitleLink>
      ) : (
        label
      )}
      {group && groupTypes ? (
        teamHref ? (
          <GroupsTreeHeaderBadgeLink
            className="treeHeaderBadge"
            data-tree-row-action
            href={teamHref}
            onClick={(e) => handleLinkClick(e)}
          >
            <GroupTypeBadge
              group={group}
              groupTypes={groupTypes as never}
              style={{ flexShrink: 0 }}
            />
          </GroupsTreeHeaderBadgeLink>
        ) : (
          <GroupTypeBadge
            group={group}
            groupTypes={groupTypes as never}
            style={{ flexShrink: 0 }}
          />
        )
      ) : null}
    </Flex>
  );
};

export default GroupsTreeHeader;
